import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { Newspaper, Eye, EyeOff, Plus, Trash2, Users, Building2, X, Send, ImagePlus, Link2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsDesk() {
  const [joiners, setJoiners] = useState([]);
  const [customAnnouncements, setCustomAnnouncements] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', message: '', department: 'ALL', visible_days: 30, image_url: '' });
  const [submitting, setSubmitting] = useState(false);
  const [imageMode, setImageMode] = useState('upload'); // 'upload' or 'url'
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/news-desk`, { headers });
      setJoiners(res.data.joiners || []);
      setCustomAnnouncements(res.data.custom_announcements || []);
      setDepartments(res.data.departments || []);
      setHiddenCount(res.data.hidden_count || 0);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load News Desk data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleVisibility = async (employeeId, currentlyHidden) => {
    try {
      await axios.post(`${API}/api/news-desk/toggle/${employeeId}`, {}, { headers });
      setJoiners(prev => prev.map(j => j.id === employeeId ? { ...j, hidden: !currentlyHidden } : j));
      setHiddenCount(prev => currentlyHidden ? prev - 1 : prev + 1);
      toast({ title: currentlyHidden ? 'Now Visible' : 'Hidden', description: `Joiner ${currentlyHidden ? 'shown on' : 'removed from'} News Desk` });
    } catch {
      toast({ title: 'Error', description: 'Failed to update visibility', variant: 'destructive' });
    }
  };

  const addCustomAnnouncement = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) return;
    setSubmitting(true);
    try {
      const payload = { ...formData, image_url: formData.image_url || null };
      const res = await axios.post(`${API}/api/news-desk/custom`, payload, { headers });
      setCustomAnnouncements(prev => [res.data, ...prev]);
      setFormData({ title: '', message: '', department: 'ALL', visible_days: 30, image_url: '' });
      setShowAddForm(false);
      toast({ title: 'Added', description: 'Custom announcement published' });
    } catch {
      toast({ title: 'Error', description: 'Failed to add announcement', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/api/news-desk/upload-image`, form, { headers });
      setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
      toast({ title: 'Uploaded', description: 'Image uploaded successfully' });
    } catch {
      toast({ title: 'Error', description: 'Failed to upload image', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const deleteCustom = async (id) => {
    try {
      await axios.delete(`${API}/api/news-desk/custom/${id}`, { headers });
      setCustomAnnouncements(prev => prev.filter(a => a.id !== id));
      toast({ title: 'Deleted', description: 'Announcement removed' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const filteredJoiners = filter === 'all' ? joiners
    : filter === 'visible' ? joiners.filter(j => !j.hidden)
    : filter === 'hidden' ? joiners.filter(j => j.hidden)
    : joiners.filter(j => j.department === filter);

  // Use departments from API (all active employee departments)
  const allDepts = departments;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-news-desk">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Newspaper className="h-6 w-6 text-indigo-600" />
            HR News Desk
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage new joiner announcements and custom bulletins visible on the Employee Portal</p>
        </div>
        <Button onClick={() => setShowAddForm(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-announcement-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Announcement
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm" data-testid="stat-total-joiners">
          <CardContent className="p-4 text-center">
            <Users className="h-6 w-6 text-indigo-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{joiners.length}</p>
            <p className="text-xs text-gray-500">New Joiners (30d)</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="stat-visible">
          <CardContent className="p-4 text-center">
            <Eye className="h-6 w-6 text-emerald-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{joiners.filter(j => !j.hidden).length}</p>
            <p className="text-xs text-gray-500">Visible</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="stat-hidden">
          <CardContent className="p-4 text-center">
            <EyeOff className="h-6 w-6 text-red-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{hiddenCount}</p>
            <p className="text-xs text-gray-500">Hidden</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="stat-departments">
          <CardContent className="p-4 text-center">
            <Building2 className="h-6 w-6 text-sky-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-900">{allDepts.length}</p>
            <p className="text-xs text-gray-500">Departments</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2" data-testid="news-desk-filters">
        {[
          { key: 'all', label: `All (${joiners.length})` },
          { key: 'visible', label: `Visible (${joiners.filter(j => !j.hidden).length})` },
          { key: 'hidden', label: `Hidden (${joiners.filter(j => j.hidden).length})` },
          ...allDepts.map(d => ({ key: d, label: d })),
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
              filter === f.key
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* New Joiners Table */}
      <Card className="border-0 shadow-sm" data-testid="joiners-table">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Users className="h-4 w-4 text-indigo-500" />
            New Joiners — Last 30 Days
            <Badge variant="outline" className="ml-auto text-xs">{filteredJoiners.length} entries</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredJoiners.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No new joiners in this view</div>
          ) : (
            <div className="divide-y">
              {filteredJoiners.map(joiner => (
                <div key={joiner.id} className={`flex items-center gap-4 px-5 py-3 transition ${joiner.hidden ? 'bg-red-50/50 opacity-60' : 'hover:bg-gray-50'}`} data-testid={`joiner-row-${joiner.employee_code}`}>
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {joiner.photo_url ? (
                      <img src={`${API}${joiner.photo_url}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold text-indigo-500">{joiner.name?.charAt(0)}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{joiner.name}</p>
                    <p className="text-xs text-gray-500">{joiner.position}</p>
                  </div>
                  <Badge className="text-[10px] bg-sky-50 text-sky-700 border-sky-200">{joiner.department}</Badge>
                  <div className="text-xs text-gray-400 w-24 text-right">
                    {new Date(joiner.join_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${joiner.hidden ? 'border-red-300 text-red-500' : 'border-emerald-300 text-emerald-600'}`}>
                    {joiner.hidden ? 'Hidden' : 'Visible'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleVisibility(joiner.id, joiner.hidden)}
                    className={`p-2 ${joiner.hidden ? 'text-emerald-600 hover:bg-emerald-50' : 'text-red-500 hover:bg-red-50'}`}
                    data-testid={`toggle-${joiner.employee_code}`}
                  >
                    {joiner.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Custom Announcements */}
      <Card className="border-0 shadow-sm" data-testid="custom-announcements-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Newspaper className="h-4 w-4 text-amber-500" />
            Custom Announcements
            <Badge variant="outline" className="ml-auto text-xs">{customAnnouncements.length} active</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {customAnnouncements.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No custom announcements</div>
          ) : (
            <div className="divide-y">
              {customAnnouncements.map(ann => (
                <div key={ann.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50" data-testid={`custom-ann-${ann.id}`}>
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {ann.image_url ? (
                      <img src={ann.image_url.startsWith('http') ? ann.image_url : `${API}${ann.image_url}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <Newspaper className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{ann.title}</p>
                    <p className="text-xs text-gray-500 line-clamp-1">{ann.message}</p>
                  </div>
                  {ann.image_url && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                      <img src={ann.image_url.startsWith('http') ? ann.image_url : `${API}${ann.image_url}`} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <Badge className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">{ann.department}</Badge>
                  <div className="text-xs text-gray-400 w-24 text-right">
                    Until {ann.visible_until}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteCustom(ann.id)}
                    className="p-2 text-red-500 hover:bg-red-50"
                    data-testid={`delete-ann-${ann.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Custom Announcement Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="add-announcement-modal">
          <div className="fixed inset-0 bg-black/40" onClick={() => setShowAddForm(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6 z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add Custom Announcement</h3>
              <button onClick={() => setShowAddForm(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={addCustomAnnouncement} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Company Town Hall Meeting"
                  className="mt-1"
                  data-testid="announcement-title-input"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Message *</Label>
                <Textarea
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Announcement details..."
                  rows={3}
                  className="mt-1"
                  data-testid="announcement-message-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">Department</Label>
                  <select
                    value={formData.department}
                    onChange={e => setFormData({ ...formData, department: e.target.value })}
                    className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                    data-testid="announcement-dept-select"
                  >
                    <option value="ALL">All Departments</option>
                    {allDepts.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Visible for (days)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={formData.visible_days}
                    onChange={e => setFormData({ ...formData, visible_days: parseInt(e.target.value) || 30 })}
                    className="mt-1"
                    data-testid="announcement-days-input"
                  />
                </div>
              </div>
              {/* Image Attachment */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <ImagePlus className="h-3.5 w-3.5" /> Image (optional)
                </Label>
                <div className="flex gap-2 mt-1.5 mb-2">
                  <button
                    type="button"
                    onClick={() => setImageMode('upload')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition ${imageMode === 'upload' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}
                    data-testid="image-mode-upload"
                  >
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageMode('url')}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition ${imageMode === 'url' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}
                    data-testid="image-mode-url"
                  >
                    <Link2 className="h-3 w-3 inline mr-1" />Paste URL
                  </button>
                </div>
                {imageMode === 'upload' ? (
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleImageUpload}
                      className="block w-full text-xs text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      data-testid="image-file-input"
                      disabled={uploading}
                    />
                    {uploading && <p className="text-xs text-indigo-500 mt-1">Uploading...</p>}
                  </div>
                ) : (
                  <Input
                    value={formData.image_url}
                    onChange={e => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    className="text-sm"
                    data-testid="image-url-input"
                  />
                )}
                {formData.image_url && (
                  <div className="mt-2 relative inline-block">
                    <img
                      src={formData.image_url.startsWith('http') ? formData.image_url : `${API}${formData.image_url}`}
                      alt="Preview"
                      className="h-20 rounded-lg object-cover border border-gray-200"
                      data-testid="image-preview"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-xs hover:bg-red-600"
                      data-testid="remove-image-btn"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={submitting || !formData.title.trim() || !formData.message.trim()} data-testid="announcement-submit-btn">
                {submitting ? 'Publishing...' : <><Send className="h-4 w-4 mr-2" /> Publish Announcement</>}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
